import { useQuery } from '@tanstack/react-query';

import type {
  DashboardRateInput,
  PunctualityStatsBackOutput,
  PunctualityStatsOutput,
  RangeInput,
} from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toRateInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

/**
 * Le back livre `PunctualityStatsBackOutput` (shape flat issu du package
 * @allocab/allocab-extranet-api-types). On reshape vers `PunctualityStatsOutput`
 * (`{ buckets, threshold }`) que le composant histogram consomme.
 */
const PUNCTUALITY_THRESHOLD_MINUTES = 10;

const adaptPunctualityStats = (data: PunctualityStatsBackOutput): PunctualityStatsOutput => ({
  buckets: [
    { range: 'under_5', count: data.nbLateLt5 ?? 0 },
    { range: '5_10', count: data.nbLateBetween5And10 ?? 0 },
    { range: '10_15', count: data.nbLateBetween10And15 ?? 0 },
    { range: '15_30', count: data.nbLateBetween15And30 ?? 0 },
    { range: 'over_30', count: data.nbLateGt30 ?? 0 },
  ],
  threshold: PUNCTUALITY_THRESHOLD_MINUTES,
});

export const usePunctualityDistribution = (input: DashboardRateInput) =>
  useQuery({
    queryKey: extranetQueryKeys.performance.punctualityDistribution(input),
    queryFn: () =>
      getExtranetApi().get<RangeInput, PunctualityStatsBackOutput>(
        '/v1/performance/punctuality-stats',
        toRateInput(input),
      ),
    select: adaptPunctualityStats,
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
  });
